import { deleteProduct, getProduct } from '../api/products';
import { correctEvaluation, getEvaluation, sendEvaluationToHuman } from '../api/url_data_agent_evaluations';
import { ADMIN_SCOPES } from '../constants/admin_scope_constants';
import { getLinkChoices } from './links_to_check_flow_helpers';
import { LIKELY_WRONG_CHECKS } from './links_to_check_helpers';
import { hasScope } from './scope_helpers';

/**
 * Writes one Likely wrong answer through the routes Live matches and the product editor already use.
 * Resolves { alreadyDone: true } when the link was already answered or a product the answer acts on is already gone.
 */
export const saveLikelyWrongAnswer = async (link, { kind, product, reason }) => {
  if (link.applied) {
    const { evaluation } = await getEvaluation(link.id);
    if (isAlreadyAnswered(evaluation)) return { alreadyDone: true };
  }
  switch (kind) {
    case 'alert_right':
      return correctEvaluation(link.id, { Product_id: link.applied.product.id });
    case 'alert_repoint':
      return link.applied.action === 'create'
        ? mergeProducts({ remove: link.applied.product, keep: product })
        : correctEvaluation(link.id, { Product_id: product.id });
    case 'undo_link':
      return sendEvaluationToHuman(link.id, { reason });
    case 'undo_add':
    case 'undo_skip':
      return sendEvaluationToHuman(link.id, { reason: getUsualUndoReason(link) });
    case 'merge':
      return mergeProducts(link.pair);
    case 'keep_both':
      return keepBoth(link);
    default:
      throw new Error(`Unknown Likely wrong answer: ${kind}`);
  }
};

export const isLikelyWrongAnswer = kind => LIKELY_WRONG_KINDS.includes(kind);

export const isUndoFirstAnswer = ({ kind }) => ['undo_link', 'undo_add', 'undo_skip'].includes(kind);

// Picking another product for one we added merges ours into it.
export const isMergeAnswer = (link, { kind }) => kind === 'merge' || (kind === 'alert_repoint' && link.applied?.action === 'create');

export const getMergeBlockedReason = (link, adminScopes) => {
  if (!hasScope(adminScopes, ADMIN_SCOPES.EDIT_PRODUCT)) return 'Merging needs product editing access. Ask your lead.';
  if (link.pair?.removeHasVariants || (link.applied?.action === 'create' && link.applied.productHasVariants)) {
    return 'This product has sizes or colours set up, so it can’t be merged here.';
  }
  return null;
};

export const getUndoneNotice = kind => UNDONE_NOTICES[kind];

// Finished from here like any Same product? card, choosing among the run's other close matches.
export const toUndoneLinkCard = link => ({
  ...link,
  cardType: 'decide',
  question: 'same_product',
  isUndone: true,
  pick: null,
  candidates: getLinkChoices(link),
  applied: null,
  alert: null
});

const LIKELY_WRONG_KINDS = ['alert_right', 'alert_repoint', 'undo_link', 'undo_add', 'undo_skip', 'merge', 'keep_both'];

const UNDONE_NOTICES = {
  undo_link: 'Undid our link. Now finish the card.',
  undo_add: 'Undid our link. Now add it as its own product.',
  undo_skip: 'Undid our link. Now pick why it should be skipped.'
};

const getUsualUndoReason = link => LIKELY_WRONG_CHECKS[link.alert?.check]?.usualUndoReason || 'wrong_product';

// No applied product means a merge already removed the one we linked.
const isAlreadyAnswered = ({ revertedAt, humanResolvedAt, reviews, appliedProduct }) => Boolean(revertedAt || humanResolvedAt || reviews?.length || !appliedProduct);

const mergeProducts = async ({ remove, keep }) => {
  const result = await deleteProduct(remove.id, { migrateToProductId: keep.id }, { validateStatus: status => status === 200 || status === 404 });
  if (result?.code === 'PRODUCT_TO_DELETE_NOT_FOUND') return { alreadyDone: true };
  if (result !== 'OK') throw result?.error || 'Could not merge these products.';
  return result;
};

const keepBoth = async link => {
  const removedProduct = await getProduct(link.pair.remove.id, {});
  if (!removedProduct?.id) return { alreadyDone: true };
  return correctEvaluation(link.id, { Product_id: link.applied.product.id });
};
